import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, unref as _unref, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, withCtx as _withCtx, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment } from "vue"

const _hoisted_1 = { class: "sc-container-section" }
const _hoisted_2 = {
  class: "sc-row",
  role: "header"
}
const _hoisted_3 = { class: "sc-form-buttons" }
const _hoisted_4 = { class: "sc-row sc-form-row school-details" }
const _hoisted_5 = { class: "sc-form-field sc-field" }
const _hoisted_6 = { class: "sc-field-value" }
const _hoisted_7 = { class: "sc-row certification-form" }
const _hoisted_8 = {
  class: "sc-row sc-form-field-groups",
  id: "certification-form"
}
const _hoisted_9 = { class: "sc-form-fields-group" }
const _hoisted_10 = { class: "sc-form-fields-group" }
const _hoisted_11 = { class: "sc-form-fields-group" }
const _hoisted_12 = { class: "sc-form-fields-group" }

import { ref, computed, onMounted, watch } from "vue";
import {
  CertificationVM,
  CertificationForm,
  CertificationFormOptions,
  UpdateCertificationRequest,
} from "@/types/certifications";
import { DisbursementVM, DisbursementFormOptions } from "@/types/disbursements";
import { useForm } from "vee-validate";
import { useCertificationForm } from "@/composables/certification/useCertificationForm";
import functions from "@/use/functions.js";
import FormField from "@/components/v2/certifications/inputs/FormField.vue";
import * as jsonpatch from "fast-json-patch";
import { Patch, PatchVM } from "@/types/json-patch";
import { formatInputDisplayValue, getFormChanges } from "@/use/formUtils";
import { $api } from "@/services/api1";
import PermissionsWrapper from "@/components/wrappers/PermissionsWrapper.vue";
import { Permissions, PermissionLevels } from "@/models/permissions";


export default /*@__PURE__*/_defineComponent({
  __name: 'CertificationForm',
  props: {
    certification: {}
  },
  emits: ["certificationUpdated"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

const mode = ref<"view" | "edit">("view");
const disbursements = ref<DisbursementVM[]>([]);
const isEditing = ref<boolean>(false);
const savingChanges = ref<boolean>(false);
const formData = ref({} as CertificationForm);
const initialValues = ref({} as CertificationForm);

const formError = ref<boolean>(false);

const productTypeId = computed<number>(
  () => props.certification?.productTypeId
);

const formOptions = computed<CertificationFormOptions>(
  () => props.certification?.formOptions
);

const certificationVM = computed<CertificationVM>(() => props.certification);

const certificationForm = computed<CertificationForm>(
  () => props.certification.form
);

const disbursementFormOptions = computed<DisbursementFormOptions>(
  () => props.certification.disbursementFormOptions
);

const createdDate = computed<string>(
  () => props.certification.details.createdDate
);

const {
  formFieldGroup1,
  formFieldGroup2,
  formFieldGroup3,
  formFieldGroup4,
  validationSchema,
  formFields,
} = useCertificationForm({
  certification: certificationVM,
  formData: formData,
  mode,
  productTypeId: productTypeId,
  formOptions: formOptions,
  createdDate: createdDate,
  disbursementFormOptions: disbursementFormOptions,
});

const showDisbursementsTable = computed<boolean>(
  () => disbursements.value.length > 0
);

const { errors, handleSubmit, defineField, validate } = useForm({
  validationSchema,
  initialValues: props.certification.initialFormValues,
});

const onSubmit = handleSubmit(async (values) => {
  savingChanges.value = true;

  const { valid, errors } = await validate();

  console.log("onSubmit", { valid, values });

  if (valid) {
    await confirmChanges();
  } else {
    functions.openModal({
      title: "Error",
      description: "Missing required data.",
    });
  }
});

async function saveChanges(patchDoc: Patch[], changes: PatchVM[]) {
  formError.value = false;

  if (mode.value === "edit") {
    await updateCertification(patchDoc, changes);
  }
}

async function updateCertification(patchDoc: Patch[], changes: PatchVM[]) {
  if (changes.length > 0) {
    const requestVM: UpdateCertificationRequest = {
      patchDocument: patchDoc,
      patchesVM: changes,
      certificationId: props.certification.form?.id || 0,
      appId: props.certification.appId || 0,
      loanId: props.certification.loanId,
      drawId: props.certification.drawId,
      certificationSequenceNumber: props.certification.form.sequenceNumber,
    };

    const response = await $api.certifications.updateCertification(requestVM);

    if (!response.success) {
      functions.openModal({
        title: "Error",
        description: "There was an issue updating the certification.",
      });
    } else {
      formData.value = response.results.form;

      initialValues.value = JSON.parse(
        JSON.stringify(response.results.initialFormValues)
      );

      isEditing.value = false;
      mode.value = "view";

      emit("certificationUpdated", response.results);
      if (response.errors && response.errors?.length > 0) {
        functions.openModal({
          title: "Alert",
          description: response.errors.join("."),
        });
      }
    }
  } else {
    isEditing.value = false;
    mode.value = "view";
  }

  savingChanges.value = false;
}

async function confirmModificationRecordGeneration() {  
  functions.openModal({
    title: 'Confirm',
    description: `Are you sure you want to generate a modification file?`,
    buttons: [
      {
        title: 'Generate',
        onClick: async () => await generateModificationFile()
      },
      {
        title: 'Back',
        onClick: () => functions.closeModal()
      }
    ]
  });
}

async function generateModificationFile() {
  const certificationId = props.certification.form?.id
  const response = await $api.certifications.generateModificationFile(certificationId);

  if (!response) {
    functions.openModal({
      title: "Alert",
      description: "Cannot generate modification file. Please try again. If the issue continues, please contact our support team.",
    });
  } else {
    functions.openModal({
      title: "Confirm",
      description: "Modification file is generated.",
    });
  }
}

function handleFieldUpdate(field: string, value: any) {
  if (mode.value !== "view") {
    formData.value[field] = value;
  }
}

function toggleEdit() {
  isEditing.value = true;
  mode.value = "edit";
}

function discardChanges() {
  formData.value = JSON.parse(JSON.stringify(initialValues.value));

  formData.value.certifiedAmount =
    props.certification.initialFormValues.certifiedAmount;

  isEditing.value = false;
  mode.value = "view";
}

function handleEditOrCancelButton() {
  if (props.certification?.isEditable) {
    if (isEditing.value) {
      discardChanges();
    } else {
      toggleEdit();
    }
  }
}

function buildPatchVM(
  patch: Patch,
  hint?: string | null,
  initialValues?: any,
  values?: any
) {
  const fieldName = patch.path.split("/")[1];
  const field = formFields.value.find((field: any) => field.name === fieldName);

  const fieldLabel = field?.label;
  const fieldType = field?.type || "";
  const fieldOptions = field?.options;
  const fieldCategory = field?.category;

  console.log("buildPatchVM", {
    patch,
    hint,
    field,
    fieldName,
    fieldLabel,
    fieldOptions,
  });

  return {
    ...patch,
    property: fieldName,
    label: fieldLabel,
    from: props.certification.initialFormValues
      ? formatInputDisplayValue(
          props.certification.initialFormValues[fieldName],
          fieldType,
          fieldOptions
        )
      : "",
    to: formatInputDisplayValue(
      formData.value[fieldName],
      fieldType,
      fieldOptions
    ),
    hint: hint,
    type: fieldType,
    category: fieldCategory,
  };
}

async function confirmChanges() {
  savingChanges.value = false;

  if (props.certification && props.certification.initialFormValues) {
    const patchDoc = jsonpatch.compare(initialValues.value, formData.value);

    let patches: PatchVM[] = [];
    if (patchDoc && patchDoc.length > 0) {
      console.log("patchDoc", patchDoc);

      for (const patch of patchDoc) {
        patches.push(buildPatchVM(patch));
      }

      let modalHtml = getFormChanges(patches, mode.value);

      functions.openModal({
        title: "Confirm",
        description: "Please review your changes before submitting...",
        html: modalHtml,
        buttons: [
          { title: "Cancel", onClick: () => functions.closeModal() },
          {
            title: "Save Changes",
            onClick: async () => {
              functions.setModalLoading();
              await saveChanges(patchDoc, patches);
            },
          },
        ],
      });
    } else {
      mode.value = "view";
      isEditing.value = false;
    }
  }
}

onMounted(() => {
  formData.value = props.certification.form;
  initialValues.value = JSON.parse(
    JSON.stringify(props.certification.initialFormValues)
  );
});

watch(
  () => props.certification.initialFormValues,
  (newValue) => {
    initialValues.value = JSON.parse(JSON.stringify(newValue));
  },
  { deep: true }
);

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _cache[1] || (_cache[1] = _createElementVNode("div", { class: "sc-container-section-header sc-section-header" }, " Certifications ", -1)),
      _createVNode(PermissionsWrapper, {
        disabled: !__props.certification.isEditable,
        showDisabledIfNotVisible: true,
        permissionIds: [_unref(Permissions).CertAndDisburse],
        permissionLevel: _unref(PermissionLevels).Update
      }, {
        default: _withCtx(() => [
          _createElementVNode("div", _hoisted_3, [
            (__props.certification.canGenerateModificationFile)
              ? (_openBlock(), _createElementBlock("button", {
                  key: 0,
                  class: "sc-button generatefile",
                  onClick: confirmModificationRecordGeneration
                }, " Generate Modification File "))
              : _createCommentVNode("", true),
            _createElementVNode("button", {
              class: "sc-button",
              onClick: handleEditOrCancelButton
            }, _toDisplayString(isEditing.value ? "Cancel" : "Edit"), 1),
            (isEditing.value)
              ? (_openBlock(), _createElementBlock("button", {
                  key: 1,
                  class: "sc-button save",
                  onClick: _cache[0] || (_cache[0] = 
//@ts-ignore
(...args) => (_unref(onSubmit) && _unref(onSubmit)(...args)))
                }, " Save "))
              : _createCommentVNode("", true)
          ])
        ]),
        _: 1
      }, 8, ["disabled", "permissionIds", "permissionLevel"])
    ]),
    _createElementVNode("div", _hoisted_4, [
      _createElementVNode("div", _hoisted_5, [
        _cache[2] || (_cache[2] = _createElementVNode("div", { class: "sc-field-label" }, "School", -1)),
        _createElementVNode("div", _hoisted_6, _toDisplayString(__props.certification.form.school), 1)
      ])
    ]),
    _createElementVNode("div", _hoisted_7, [
      _createElementVNode("div", _hoisted_8, [
        _createElementVNode("div", _hoisted_9, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(formFieldGroup1), (field) => {
            return (_openBlock(), _createElementBlock("div", {
              class: "sc-form-field sc-field",
              key: field.name
            }, [
              _createVNode(FormField, {
                isSubmitting: savingChanges.value,
                isEditing: isEditing.value,
                field: field,
                mode: isEditing.value ? 'edit' : 'view',
                modelValue: formData.value[field.name],
                "onUpdate:modelValue": [($event: any) => ((formData.value[field.name]) = $event), ($event: any) => (handleFieldUpdate(field.name, $event))]
              }, null, 8, ["isSubmitting", "isEditing", "field", "mode", "modelValue", "onUpdate:modelValue"])
            ]))
          }), 128))
        ]),
        _createElementVNode("div", _hoisted_10, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(formFieldGroup2), (field) => {
            return (_openBlock(), _createElementBlock("div", {
              class: "sc-form-field sc-field",
              key: field.name
            }, [
              _createVNode(FormField, {
                isSubmitting: savingChanges.value,
                isEditing: isEditing.value,
                field: field,
                mode: isEditing.value ? 'edit' : 'view',
                modelValue: formData.value[field.name],
                "onUpdate:modelValue": [($event: any) => ((formData.value[field.name]) = $event), ($event: any) => (handleFieldUpdate(field.name, $event))]
              }, null, 8, ["isSubmitting", "isEditing", "field", "mode", "modelValue", "onUpdate:modelValue"])
            ]))
          }), 128))
        ]),
        _createElementVNode("div", _hoisted_11, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(formFieldGroup3), (field) => {
            return (_openBlock(), _createElementBlock("div", {
              class: "sc-form-field sc-field",
              key: field.name
            }, [
              _createVNode(FormField, {
                isSubmitting: savingChanges.value,
                isEditing: isEditing.value,
                field: field,
                mode: isEditing.value ? 'edit' : 'view',
                modelValue: formData.value[field.name],
                "onUpdate:modelValue": [($event: any) => ((formData.value[field.name]) = $event), ($event: any) => (handleFieldUpdate(field.name, $event))]
              }, null, 8, ["isSubmitting", "isEditing", "field", "mode", "modelValue", "onUpdate:modelValue"])
            ]))
          }), 128))
        ]),
        _createElementVNode("div", _hoisted_12, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(formFieldGroup4), (field) => {
            return (_openBlock(), _createElementBlock("div", {
              class: "sc-form-field sc-field",
              key: field.name
            }, [
              _createVNode(FormField, {
                isSubmitting: savingChanges.value,
                isEditing: isEditing.value,
                field: field,
                mode: isEditing.value ? 'edit' : 'view',
                modelValue: formData.value[field.name],
                "onUpdate:modelValue": [($event: any) => ((formData.value[field.name]) = $event), ($event: any) => (handleFieldUpdate(field.name, $event))]
              }, null, 8, ["isSubmitting", "isEditing", "field", "mode", "modelValue", "onUpdate:modelValue"])
            ]))
          }), 128))
        ])
      ])
    ])
  ]))
}
}

})